// PDF Utilities for Arabic text support
import { jsPDF } from 'jspdf'
import { configureArabicFont, addArabicText, processArabicText } from './arabicFont'
import { getCountryConfig } from '../services/countryConfigService'
import { isEgyptCountry } from './currencyUtils'
import i18n from '../i18n'

// Re-export Arabic font functions for backward compatibility
export { configureArabicFont, addArabicText, processArabicText } from './arabicFont'

// Helper function to create a styled header
export const createStyledHeader = (doc: jsPDF, title: string, backgroundColor: string) => {
  // Add colored background
  doc.setFillColor(backgroundColor)
  doc.rect(0, 0, 210, 30, 'F')
  
  // Add title
  addArabicText(doc, title, 105, 20, {
    align: 'center',
    fontSize: 24,
    fontStyle: 'bold',
    color: [55, 65, 81] // gray-700
  })
  
  // Add separator line
  doc.setDrawColor(107, 114, 128) // gray-500
  doc.setLineWidth(0.5)
  doc.line(20, 35, 190, 35)
}

// Helper function to create a styled box
export const createStyledBox = (
  doc: jsPDF, 
  x: number, 
  y: number, 
  width: number, 
  height: number, 
  backgroundColor: string,
  borderColor?: string
) => {
  if (backgroundColor) {
    doc.setFillColor(backgroundColor)
    doc.rect(x, y, width, height, 'F')
  }
  
  if (borderColor) {
    doc.setDrawColor(borderColor)
    doc.setLineWidth(0.5)
    doc.rect(x, y, width, height, 'S')
  }
}

// Helper function to format currency
export const formatCurrency = (amount: number): string => {
  // i18next@25 typings assume TS5; CRA uses TS4 — cast keeps runtime behavior
  const t = i18n.t as (key: string) => string
  const label = isEgyptCountry(getCountryConfig())
    ? t('currency.egyptSymbol')
    : 'ريال'
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(amount || 0)} ${label}`
}

// Helper function to format date based on language
export const formatDate = (dateString: string, language: string = "ar"): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    
    const countryConfig = getCountryConfig()
    const arLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
    const locale = language === 'ar' ? arLocale : 'en-US'
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date)
  } catch {
    return dateString
  }
}

// Enhanced Arabic text rendering using HTML canvas
export const renderArabicTextToCanvas = async (
  text: string,
  fontSize: number = 12,
  fontFamily: string = 'Arial, sans-serif',
  color: string = '#000000'
): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      resolve(text) // Fallback to original text
      return
    }

    // Set canvas size based on text
    canvas.width = 800
    canvas.height = 100
    
    // Configure text rendering
    ctx.font = `${fontSize}px ${fontFamily}`
    ctx.fillStyle = color
    ctx.textAlign = 'right'
    ctx.direction = 'rtl'
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Render Arabic text
    ctx.fillText(text, canvas.width - 20, 50)
    
    // Convert to data URL
    const dataURL = canvas.toDataURL()
    resolve(dataURL)
  })
}

// Alternative solution: Create a complete HTML-based invoice
export const generateInvoiceHTML = (orderDetails: any, calculations: any, calculateItemTotalPrice?: (item: any) => number): string => {
  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>فاتورة الطلب</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Noto Sans Arabic', Arial, sans-serif;
          direction: rtl;
          background: white;
          color: #374151;
          line-height: 1.6;
        }
        
        .invoice {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: white;
        }
        
        .header {
          background: #E6F4EA;
          padding: 20px;
          text-align: center;
          margin-bottom: 20px;
          border-radius: 8px;
        }
        
        .header h1 {
          color: #374151;
          font-size: 24px;
          font-weight: 700;
        }
        
        .info-box {
          background: #F3F4F6;
          padding: 15px;
          margin-bottom: 20px;
          border-radius: 8px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        
        .section-title {
          font-size: 18px;
          font-weight: 700;
          color: #059669;
          margin-bottom: 10px;
          border-bottom: 2px solid #059669;
          padding-bottom: 5px;
        }
        
        .products-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        
        .products-table th,
        .products-table td {
          padding: 12px;
          text-align: right;
          border-bottom: 1px solid #E5E7EB;
        }
        
        .products-table th {
          background: #059669;
          color: white;
          font-weight: 700;
        }
        
        .products-table tr:nth-child(even) {
          background: #F8FAFC;
        }
        
        .totals {
          background: #F3F4F6;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 4px 0;
        }
        
        .grand-total {
          background: #059669;
          color: white;
          padding: 15px;
          border-radius: 8px;
          font-size: 18px;
          font-weight: 700;
          text-align: center;
        }
        
        .note {
          background: #F3F4F6;
          padding: 15px;
          border-radius: 8px;
          font-size: 12px;
          color: #6B7280;
          margin-top: 20px;
        }
        
        @media print {
          body { margin: 0; }
          .invoice { max-width: none; }
        }
      </style>
    </head>
    <body>
      <div class="invoice">
        <div class="header">
          <h1>فاتورة الطلب</h1>
        </div>
        
        <div class="info-box">
          <div class="info-row">
            <strong>رقم الطلب:</strong>
            <span>${orderDetails.order_number || orderDetails.id}</span>
          </div>
          <div class="info-row">
            <strong>التاريخ:</strong>
            <span>${formatDate(orderDetails.created_at || new Date().toISOString())}</span>
          </div>
        </div>
        
        <h2 class="section-title">بيانات العميل</h2>
        <div class="info-box">
          <div class="info-row">
            <strong>الاسم:</strong>
            <span>${orderDetails.user?.name || "غير متوفر"}</span>
          </div>
          <div class="info-row">
            <strong>الهاتف:</strong>
            <span>${orderDetails.customer_phone || orderDetails.user?.phone || "غير متوفر"}</span>
          </div>
          <div class="info-row">
            <strong>الفرع:</strong>
            <span>${typeof orderDetails.branch === 'string' ? orderDetails.branch : orderDetails.branch?.name || "غير متوفر"}</span>
          </div>
          <div class="info-row">
            <strong>طريقة الدفع:</strong>
            <span>${orderDetails.payment_method || "غير متوفر"}</span>
          </div>
        </div>
        
        ${orderDetails.table ? `
          <h2 class="section-title">تفاصيل الطاولة</h2>
          <div class="info-box">
            <div class="info-row">
              <strong>رقم الطاولة:</strong>
              <span>${orderDetails.table.name || "غير متوفر"}</span>
            </div>
            <div class="info-row">
              <strong>سعر الطاولة:</strong>
              <span>${formatCurrency(calculations.tablePrice)}</span>
            </div>
          </div>
        ` : ''}
        
        <h2 class="section-title">المنتجات</h2>
        <table class="products-table">
          <thead>
            <tr>
              <th>المنتج</th>
              <th>الكمية</th>
              <th>السعر</th>
            </tr>
          </thead>
          <tbody>
            ${orderDetails.items?.map((item: any, index: number) => `
              <tr>
                <td>
                  <strong>${item.meals?.[0]?.name || item.name || "منتج"}</strong>
                  ${item.meals?.[0]?.additionals?.length > 0 ? `
                    <br><small style="color: #6B7280;">- ${item.meals[0].additionals.map((addon: any) => addon.name).join(' - ')}</small>
                  ` : ''}
                </td>
                <td>${item.quantity}</td>
                <td>${formatCurrency(calculateItemTotalPrice ? calculateItemTotalPrice(item) : parseFloat(item.price) * item.quantity || 0)}</td>
              </tr>
            `).join('') || ''}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">
            <strong>المجموع الفرعي:</strong>
            <span>${formatCurrency(calculations.subtotal)}</span>
          </div>
          ${calculations.tablePrice > 0 ? `
            <div class="total-row">
              <strong>سعر الطاولة:</strong>
              <span>${formatCurrency(calculations.tablePrice)}</span>
            </div>
          ` : ''}
          ${calculations.deliveryPrice > 0 ? `
            <div class="total-row">
              <strong>رسوم التوصيل:</strong>
              <span>${formatCurrency(calculations.deliveryPrice)}</span>
            </div>
          ` : ''}
          ${calculations.discount > 0.01 ? `
            <div class="total-row" style="color: #22C55E;">
              <strong>خصم الكوبون:</strong>
              <span>-${formatCurrency(calculations.discount)}</span>
            </div>
          ` : ''}
          <div class="total-row">
            <strong>الضريبة ${orderDetails.tax_percentage || 15}%:</strong>
            <span>${formatCurrency(calculations.taxValue)}</span>
          </div>
        </div>
        
        <div class="grand-total">
          المجموع الكلي: ${formatCurrency(calculations.totalPrice)}
        </div>
        
        <div class="note">
          <p><strong>ملاحظة:</strong> الفاتورة الضريبية ستكون عند استلام الطلب من الفرع.</p>
          <p>استرجاع مبلغ التأمين خلال 15 يوماً. الضريبة لا تشمل مبلغ التأمين.</p>
        </div>
      </div>
    </body>
    </html>
  `
}
